import { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { getIsUserLoggedIn } from '../../data/user/userSelectors'

/**
 * The name of the BroadcastChannel used for session synchronization across tabs/windows.
 */
export const SESSION_SYNC_CHANNEL = 'session-sync-channel'

/**
 * The message type sent when a session is terminated and should be synchronized across tabs/windows.
 */
export const SESSION_TERMINATED_MESSAGE = 'SESSION_TERMINATED'

/**
 * A React hook that synchronizes session state across multiple tabs/windows.
 *
 * This hook listens for session termination messages from other tabs/windows
 * via a BroadcastChannel. When a SESSION_TERMINATED_MESSAGE is received and
 * the user is logged in, it redirects the current tab to the logout page.
 *
 * This ensures that when a user logs out in one tab or their session expires,
 * all other open tabs of the application will also log out, maintaining session
 * consistency across the entire browser.
 */
export const useSessionSync = () => {
  const dispatch = useDispatch()
  const isLoggedIn = useSelector(getIsUserLoggedIn)

  useEffect(() => {
    const channel = new BroadcastChannel(SESSION_SYNC_CHANNEL)

    const handleSessionMessage = (event: MessageEvent) => {
      if (event.data.type === SESSION_TERMINATED_MESSAGE && isLoggedIn) {
        window.location.href = '/logout' // logout user for all sessions
      }
    }

    channel.onmessage = handleSessionMessage

    return () => {
      channel.close()
    }
  }, [dispatch, isLoggedIn])
}

/**
 * Broadcasts a session termination message to all open tabs/windows of the application.
 *
 * This function creates a BroadcastChannel using the SESSION_SYNC_CHANNEL constant,
 * sends a SESSION_TERMINATED_MESSAGE to notify all other tabs that the session has been
 * terminated, and then closes the channel. When other tabs receive this message via
 * the useSessionSync hook, they will redirect to the logout page if the user is logged in.
 *
 * Use this function when you need to terminate the user's session across all open instances
 * of the application, such as during logout, session timeout, or authentication failures.
 */
export const broadcastSessionTermination = () => {
  const channel = new BroadcastChannel(SESSION_SYNC_CHANNEL)
  channel.postMessage({ type: SESSION_TERMINATED_MESSAGE })
  channel.close()
}
